/**
 * Copyright © 2026 Adnuntius AS.
 */
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  signal,
  ViewChild
} from '@angular/core';
import {notifySignalView} from '../../common/util/template-state';
import * as _ from 'lodash';
import * as L from 'leaflet';
import 'leaflet-draw';

import template from './geospatial-targeting.component.html';
import {ADN_TARGETING_TYPES} from '../targeting-constants';
import {TargetingTemplateHook} from './targeting-template.component';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';

interface LayerDatum {
  id: number;
  area: number;
  areaView: number;
  decimalPoints: number;
  color: string;
  geoJson: any;
}

function leafletApi(): any {
  return L;
}

@Component({
  standalone: false,
  selector: 'adn-geospatial-targeting',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GeospatialTargetingComponent implements OnInit, OnDestroy {
  @Input() model: any[];
  @Input() tabController: any;
  @Input() overridableTargeting: any;
  @Input() targetType: any = ADN_TARGETING_TYPES.geospatial;
  @Input() underModel: any;
  @Input() mode: string;
  @Input() isLockedDown: boolean;
  @Input() mandatory: boolean;

  mapReady = false;
  layerData: LayerDatum[] = [];
  layerInitialised = false;
  readonly viewRevision = signal(0);

  private map: any;
  private editableLayers: any;
  private drawControl: any;
  private colorSet = ['blue', '#333', 'brown', 'purple', '#666', 'cyan'];
  private colorIndex = -1;
  private currentColor = this.colorSet[0];
  private mapInitTimer: any;
  private mapResizeTimers: any[] = [];
  private mapContainerElement: HTMLElement | null = null;
  private destroyed = false;

  readonly templateHook: TargetingTemplateHook = {
    getTemplateData: () => {
      this.prepareForSave();
      return {
        vm: undefined,
        model: _.cloneDeep(this.model),
        constant: ADN_TARGETING_TYPES.geospatial
      };
    },
    copyTemplate: (tpl: any) => this.copyTemplate(tpl)
  };

  @ViewChild('mapContainer')
  set mapContainer(el: ElementRef | undefined) {
    if (!el || this.destroyed) {
      return;
    }
    this.mapContainerElement = el.nativeElement;
    if (this.mapReady && !this.map) {
      this.initMap(this.mapContainerElement);
      return;
    }
    if (this.map) {
      this.scheduleMapResize();
    }
  }

  constructor(
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly ngZone: NgZone
  ) {
  }

  ngOnInit(): void {
    this.ensureModel();

    if (!this.tabController) {
      return;
    }

    const prepareForSave = (isBeforeSubmit?: boolean) => this.prepareForSave(isBeforeSubmit);
    const copyTemplate = (tpl: any) => this.copyTemplate(tpl);
    this.tabController.callBeforeSubmit(() => prepareForSave(true));
    this.tabController.registerAllTabs(() => prepareForSave());
    this.tabController.register(ADN_TARGETING_TYPES.geospatial.widget, () => this.onTabSelect(), copyTemplate);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.mapInitTimer) {
      clearTimeout(this.mapInitTimer);
      this.mapInitTimer = null;
    }
    this.clearMapResizeTimers();
    this.destroyMap();
  }

  clearEntries(): void {
    this.ensureModel();
    this.model.length = 0;
    this.layerData = [];
    if (this.editableLayers) {
      this.editableLayers.clearLayers();
    }
    notifySignalView(this.viewRevision);
  }

  centerOnLayer(layer: LayerDatum): void {
    if (!this.editableLayers || !this.map) {
      return;
    }
    this.editableLayers.getLayers().forEach((l: any) => {
      if (l._leaflet_id === layer.id) {
        this.map.panTo(l.getBounds().getCenter());
      }
    });
  }

  formatArea(layer: LayerDatum): string {
    return Number(layer.areaView).toFixed(layer.decimalPoints);
  }

  private onTabSelect(): void {
    if (!this.map && this.mapContainerElement) {
      this.initMap(this.mapContainerElement);
    }
    if (this.mapReady) {
      this.scheduleMapResize();
      return;
    }
    if (this.mapInitTimer) {
      clearTimeout(this.mapInitTimer);
    }
    this.mapInitTimer = setTimeout(() => {
      this.mapReady = true;
      notifySignalView(this.viewRevision);
      this.scheduleMapResize();
    }, 500);
  }

  private initMap(container: HTMLElement): void {
    const L = leafletApi();
    if (!L || !L.map || !L.Control || !L.Control.Draw) {
      return;
    }

    const networkDefaults = this.LocalNetworkProfile.getDefaults() || {};
    const center = [networkDefaults.geoLat, networkDefaults.geoLng];
    const zoom = networkDefaults.geoZoom || 5;

    this.ngZone.runOutsideAngular(() => {
      this.map = L.map(container, {
        center,
        zoom
      });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors'
      }).addTo(this.map);

      this.editableLayers = new L.FeatureGroup();
      this.map.addLayer(this.editableLayers);
      this.map.zoomControl.setPosition('topright');

      this.drawControl = new L.Control.Draw({
        position: 'topright',
        draw: {
          polyline: false,
          circle: false,
          marker: false,
          circlemarker: false,
          polygon: {
            allowIntersection: false,
            drawError: {
              color: 'red',
              timeout: 2000
            },
            showArea: true
          },
          rectangle: {
            showArea: true
          }
        },
        edit: {
          featureGroup: this.editableLayers,
          edit: true,
          remove: true
        }
      });
      this.map.addControl(this.drawControl);

      this.map.on(L.Draw.Event.CREATED, (e: any) => {
        const layer = e.layer;
        this.editableLayers.addLayer(layer);
        this.layerData = [...this.layerData, this.getLayerData(layer)];
        this.updateColor();
        notifySignalView(this.viewRevision);
      });

      this.map.on(L.Draw.Event.DELETED, (e: any) => {
        e.layers.eachLayer((dl: any) => {
          _.remove(this.layerData, (ml) => ml.id === dl._leaflet_id);
        });
        this.layerData = [...this.layerData];
        notifySignalView(this.viewRevision);
      });

      this.map.on(L.Draw.Event.EDITED, (e: any) => {
        e.layers.eachLayer((dl: any) => {
          const modelElementIndex = _.findIndex(this.layerData, (ml) => ml.id === dl._leaflet_id);
          if (modelElementIndex > -1) {
            this.layerData[modelElementIndex] = this.getLayerData(dl);
          }
        });
        this.layerData = [...this.layerData];
        notifySignalView(this.viewRevision);
      });

      this.loadGeometriesFromModel();
      this.focusLargestLayer();
      this.scheduleMapResize();
    });
  }

  private loadGeometriesFromModel(): void {
    const L = leafletApi();
    if (!this.map || !this.editableLayers || !L) {
      return;
    }
    this.editableLayers.clearLayers();
    this.layerData = [];
    this.layerInitialised = false;

    _.forEach(_.get(this.model, [0, 'definition', 'geometries']) || [], (geo: any) => {
      const featureCollection = {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: {},
          geometry: {type: 'GeometryCollection', geometries: [geo]}
        }]
      };
      L.geoJson(featureCollection, {
        onEachFeature: (_feature, layer: any) => {
          if (layer.getLayers) {
            layer.getLayers().forEach((l: any) => {
              this.updateColor();
              l.setStyle({color: this.currentColor});
              this.editableLayers.addLayer(l);
              this.layerData.push(this.getLayerData(l));
            });
          }
        }
      });
    });

    this.layerInitialised = true;
    this.updateColor();
    notifySignalView(this.viewRevision);
  }

  private focusLargestLayer(): void {
    let largestLayer: LayerDatum;
    _.forEach(this.layerData, (l) => {
      if (!largestLayer || largestLayer.area < l.area) {
        largestLayer = l;
      }
    });
    if (!largestLayer || !this.map || !this.editableLayers) {
      return;
    }
    const mapLayer: any = _.find(this.editableLayers.getLayers(), (l: any) => l._leaflet_id === largestLayer.id);
    if (mapLayer) {
      this.map.panTo(mapLayer.getBounds().getCenter());
    }
    if (largestLayer.area > 300000000) {
      this.map.setZoom(4);
    } else if (largestLayer.area < 10000) {
      this.map.setZoom(15);
    }
  }

  private getLayerData(l: any): LayerDatum {
    const L = leafletApi();
    const latLngs = l.getLatLngs();
    const ring = _.isArray(latLngs[0]) ? latLngs[0] : latLngs;
    const rawArea = L.GeometryUtil.geodesicArea(ring);
    const displayArea = rawArea / 1000000;
    const decimalPoints = displayArea < 10 ? 3 : 0;
    return {
      id: l._leaflet_id,
      area: rawArea,
      areaView: displayArea,
      decimalPoints,
      color: l.options.color,
      geoJson: l.toGeoJSON()
    };
  }

  private updateColor(): void {
    this.colorIndex++;
    this.colorIndex = this.colorIndex % this.colorSet.length;
    this.currentColor = this.colorSet[this.colorIndex];
    if (!this.drawControl) {
      return;
    }
    this.drawControl.setDrawingOptions({
      polygon: {
        shapeOptions: {
          color: this.currentColor
        }
      },
      rectangle: {
        shapeOptions: {
          color: this.currentColor
        }
      }
    });
  }

  private prepareForSave(isBeforeSubmit?: boolean): void {
    this.ensureModel();
    if ((!isBeforeSubmit && this.layerData.length < 1) || !this.layerInitialised) {
      return;
    }

    this.model.length = 0;
    const definition = {geometries: [] as any[], type: 'GeometryCollection'};
    _.forEach(this.layerData, (layer) => {
      definition.geometries.push({
        type: 'Polygon',
        coordinates: layer.geoJson.geometry.coordinates
      });
    });
    this.model.push({definition});
  }

  private copyTemplate(tpl: any): void {
    this.clearEntries();
    const copied = _.cloneDeep(_.get(tpl, ['data', 'model'])) || [];
    this.ensureModel();
    this.model.length = 0;
    _.forEach(copied, (item) => this.model.push(item));
    if (this.map) {
      this.loadGeometriesFromModel();
      this.focusLargestLayer();
      this.scheduleMapResize();
    }
  }

  private ensureModel(): void {
    if (!_.isArray(this.model)) {
      return;
    }
  }

  private destroyMap(): void {
    if (this.map) {
      this.map.off();
      this.map.remove();
      this.map = null;
      this.editableLayers = null;
      this.drawControl = null;
    }
    this.mapContainerElement = null;
  }

  private scheduleMapResize(): void {
    if (!this.map) {
      return;
    }
    this.clearMapResizeTimers();
    this.ngZone.runOutsideAngular(() => {
      _.forEach([0, 100, 250, 500], (delay) => {
        const timer = setTimeout(() => {
          if (!this.map || !this.mapContainerElement) {
            return;
          }
          if (this.mapContainerElement.offsetWidth < 1 || this.mapContainerElement.offsetHeight < 1) {
            return;
          }
          this.map.invalidateSize();
        }, delay);
        this.mapResizeTimers.push(timer);
      });
    });
  }

  private clearMapResizeTimers(): void {
    _.forEach(this.mapResizeTimers, (timer) => clearTimeout(timer));
    this.mapResizeTimers = [];
  }
}
